/* eslint-disable max-lines-per-function */
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useKalenderContext } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/desktop/KalenderContext';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Fragment, useEffect, useRef, useState } from 'react';
import { ArbeitsbereichKalenderStation as ArbeitsbereichKalenderStationType } from '@/domain/mitarbeiter/features/rotationsplan/entities/Station';
import { StyleProps } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/desktop/components/station/types';
import { Arbeitsbereich } from '@/domain/mitarbeiter/features/rotationsplan/entities/Arbeitsbereich';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

export function ArbeitsbereichKalenderStation({
  station,
  arbeitsbereich,
}: {
  station: ArbeitsbereichKalenderStationType;
  arbeitsbereich: Arbeitsbereich;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const { isScrolling } = useKalenderContext();
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState({ x: 0 });
  const { t } = useTranslation();

  const handleMouseEnter = (event: React.MouseEvent) => {
    setOpen(true);
    setCursor({
      x: event.clientX - ref.current.getBoundingClientRect().left,
    });
  };

  const handleMouseLeave = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (isScrolling && open) {
      setOpen(false);
    }
  }, [isScrolling, open]);

  return (
    <div
      className={`col-start-(--col-start) col-end-(--col-end) p-1`}
      style={
        {
          '--col-start': station.getColStart(),
          '--col-end': station.getColEnd(),
        } as StyleProps
      }>
      <div className="relative w-full h-full" ref={ref}>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger
            style={{
              position: 'absolute',
              left: cursor.x,
              bottom: 0,
              height: 0.5,
              width: 0.5,
              color: 'white',
              outline: 'none',
            }}
          />
          <div
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            className={`w-full h-full bg-(--bg-color) border-(--border-color) border border-solid rounded-md flex items-center justify-start p-2`}
            style={
              {
                '--bg-color': arbeitsbereich.getFillColor(),
                '--border-color': arbeitsbereich.getBorderColor(),
              } as StyleProps
            }>
            <div className="text-xs truncate">{station.mitarbeiter.length}</div>
          </div>
          <PopoverContent className="bg-white flex flex-col gap-2 p-3 text-sm w-75">
            <div className="flex flex-col mb-2 gap-1">
              <div className="flex gap-2 items-center">
                <FontAwesomeIcon
                  icon={faCircle}
                  color={arbeitsbereich.getBorderColor()}
                />
                <div className="font-semibold">{arbeitsbereich.name}</div>
              </div>
              <div className="text-xs text-neutrals-800">
                {dayjs(station.von).format('DD.MM.YYYY') +
                  '–' +
                  dayjs(station.bis).format('DD.MM.YYYY') +
                  ' ⋅ ' +
                  station.mitarbeiter.length +
                  ' ' +
                  t('entities.employee')}
              </div>
            </div>
            {station.mitarbeiter.map((m, index) => {
              return (
                <Fragment key={m.id}>
                  <div key={m.id} className="flex gap-2">
                    <Avatar>
                      {m.imgUrl ? (
                        <AvatarImage
                          alt={m.vorname + ' ' + m.nachname}
                          src={m.imgUrl}
                        />
                      ) : (
                        <AvatarFallback>
                          {m.vorname[0] + m.nachname[0]}
                        </AvatarFallback>
                      )}
                    </Avatar>
                    <div className="hidden md:flex md:flex-col min-w-0">
                      <div className="text-sm text-neutrals-900 truncate">
                        {m.vorname + ' ' + m.nachname}
                      </div>
                      <div className="text-xs text-neutrals-700">
                        {m.lehrjahr + ' ⋅ ' + m.beruf.abkuerzung}
                      </div>
                    </div>
                  </div>
                  {index < station.mitarbeiter.length - 1 && (
                    <div className="bg-neutrals-300 w-full h-px" />
                  )}
                </Fragment>
              );
            })}
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
